import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../components/Card/Card";
import { Logo } from "../components/Logo/Logo";
import "./Verify.css";
import { config } from "../config";

type VerifyStatus = "verifying" | "success" | "error";

interface VerifyResponse {
  message?: string;
  error?: string;
}

const extract_error = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "Verification failed. Please try again or contact support.";
};

export const Verify: React.FC = () => {
  const [search_params] = useSearchParams();
  const [status, set_status] = useState<VerifyStatus>("verifying");
  const [message, set_message] = useState<string>("");

  useEffect(() => {
    const verify_token = async (): Promise<void> => {
      const token = search_params.get("token");

      if (!token) {
        set_status("error");
        set_message("No verification token provided.");
        return;
      }

      try {
        const endpoint_url = `${config.registration.baseUrl}/api/verify?token=${encodeURIComponent(token)}`;

        const response = await fetch(
          endpoint_url,
          {
            method: "GET",
            credentials: "omit",
          }
        );

        let data: VerifyResponse = {};
        const token_fragment = token.substring(0, 6);

        try {
          data = (await response.json()) as VerifyResponse;
        } catch (parse_error) {
          throw new Error(
            `Verification request failed with non-JSON response. Endpoint: ${endpoint_url}, Token: ${token_fragment}..., Status: ${response.status}`
          );
        }

        if (!response.ok) {
          throw new Error(
            data.error ||
              `Verification failed. Endpoint: ${endpoint_url}, Token: ${token_fragment}..., Status: ${response.status}`
          );
        }

        set_status("success");
        set_message(
          data.message || "Your email has been verified successfully!"
        );
      } catch (error) {
        set_status("error");
        set_message(extract_error(error));
      }
    };

    verify_token();
  }, [search_params]);

  return (
    <div className="verify-page">
      <div className="verify-container">
        <div className="verify-header">
          <Logo variant="dark" size="medium" />
          <h2>
            {status === "verifying"
              ? "Verifying Your Account"
              : status === "success"
                ? "Verification Successful"
                : "Verification Failed"}
          </h2>
        </div>

        <Card className="verify-card" variant="bordered" padding="large">
          {status === "verifying" && (
            <div className="verify-spinner">
              <div className="spinner" />
              <p>Please wait while we verify your account...</p>
            </div>
          )}

          {status === "success" && (
            <div className="verify-message verify-message--success">
              <p>{message}</p>
              <a href="/" className="verify-link">
                Return to Home
              </a>
            </div>
          )}

          {status === "error" && (
            <div className="verify-message verify-message--error">
              <p>{message}</p>
              <a href="/signup" className="verify-link">
                Try Signing Up Again
              </a>
              <a href="/reset-password" className="verify-link">
                Reset Password
              </a>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Verify;
